import axios from 'axios';
import {
  Collection,
  DownloadRequest,
  DropdownDataResults,
  ResourceData,
  ResourceDetails,
  SearchResults,
  User,
  UserAPIError,
} from './types';

export const fetchCollections = (): Promise<Collection[]> =>
  axios
    .get(`${import.meta.env.VITE_API_URL}/publicCollections`)
    .then((res) => res.data.data);

export const fetchCollectionResourcesById = (
  id: number,
  page: number,
  sortBy: string = 'relevance'
): Promise<SearchResults> =>
  axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/listCollectionResources/${id}/?page=${page}&sort=${sortBy}`
    )
    .then((res) => res.data.data);

export const fetchCollection = (collectionId: number): Promise<Collection> =>
  axios
    .get(`${import.meta.env.VITE_API_URL}/collection/${collectionId}`)
    .then((res) => {
      if (res.data.success) {
        return res.data.data;
      } else {
        throw "Couldn't find collection details";
      }
    });

export const searchResources = (
  keywords: string,
  page: number,
  sortBy: string = 'relevance'
): Promise<SearchResults> =>
  axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/searchWithPreviews?q=${keywords}&page=${page}&sort=${sortBy}`
    )
    .then((res) => res.data.data);

export const fetchResourceById = (id: number): Promise<ResourceDetails> =>
  axios
    .get(`${import.meta.env.VITE_API_URL}/resourceDetails/${id}`)
    .then((res) => res.data.data);

export const dropdownData = (): Promise<DropdownDataResults> =>
  axios
    .get(`${import.meta.env.VITE_API_URL}/dropdownData`)
    .then((res) => res.data.data);

export const fetchUserCollections = (): Promise<Collection[]> =>
  axios
    .get(`${import.meta.env.VITE_API_URL}/user/listCollections`)
    .then((res) => {
      if (res.data.success) {
        return res.data.data;
      } else {
        throw {
          message: 'Not logged in',
          forceLogout: true,
        } as UserAPIError;
      }
    });

export const login = (username: string, password: string): Promise<User> =>
  axios
    .post(`${import.meta.env.VITE_API_URL}/user/login`, { username, password })
    .then((res) => {
      if (res.data.success) {
        return { username, loggedIn: true, user: res.data.user } as User;
      } else {
        throw "Couldn't log you in with those credentials";
      }
    });

export const logout = (): Promise<boolean> =>
  axios
    .get(`${import.meta.env.VITE_API_URL}/user/logout`)
    .then((res) => res.data.success);

export const addResourceToCollection = (
  collectionId: number,
  resourceId: number
): Promise<boolean> =>
  axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/user/addResource?collection_id=${collectionId}&resource_id=${resourceId}`
    )
    .then((res) => res.data.success);

export const createNewCollection = (collectionName: string): Promise<number> =>
  axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/user/createCollection?collection_name=${collectionName}`
    )
    .then((res) => {
      if (res.data.success) {
        return res.data.data;
      }
      throw 'Failed to create collection';
    });

export const deleteCollection = (collectionId: number): Promise<boolean> =>
  axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/user/deleteCollection?collection_id=${collectionId}`
    )
    .then((res) => res.data.success);

export const removeResourceFromCollection = (
  collectionId: number,
  resourceId: number
) =>
  axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/user/removeResource?collection_id=${collectionId}&resource_id=${resourceId}`
    )
    .then((res) => {
      if (res.data.success) return true;
      throw 'Failed to delete resource';
    });

export const fetchCsrfToken = (): Promise<string> =>
  axios.get(`${
        import.meta.env.VITE_TOKEN_URL
      }`).then((res) => res.data);

export const createDownloadRequest = (
  image_size: string,
  resourceData?: ResourceData[],
  collection_id?: number
): Promise<number> =>
  axios
    .post(`${import.meta.env.VITE_API_URL}/createDownloadRequest`, {
      resource_data: resourceData,
      collection_id,
      image_size,
    })
    .then((res) => {
      if (res.data.success) {
        return res.data.download_request_id as number;
      } else {
        throw "Couldn't log you in with those credentials";
      }
    });

export const fetchDownloadRequest = (
  downloadRequestId: number
): Promise<DownloadRequest> => {
  return axios
    .get(
      `${
        import.meta.env.VITE_API_URL
      }/downloadRequestStatus/${downloadRequestId}`
    )
    .then((res) => {
      if (res.data.success) {
        return res.data as DownloadRequest;
      } else {
        throw "Couldn't find download request";
      }
    });
};
